import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { PhoneArrowUpRightIcon } from "@heroicons/react/24/outline";

import { wilmaSAVCustomerId } from "../../../../config";
import { useAuth } from "../../../../contexts/AuthContext";
import useCallQueue from "../../../../data/useCallQueue";
import useInitialPriceEstimate from "../../../../data/useInitialPriceEstimate";
import useSav from "../../../../data/useSav";
import useVehiclePackageData from "../../../../data/useVehiclePackageData";
import useVehiclesMutations from "../../../../data/useVehiclesMutations";
import { CallOutcome, ICallSheet, UpNextFilter } from "../../../../models/CallQueue";
import { VehiclePackage } from "../../../../models/VehiclePackageData";
import { TelephonyAdapter, TelLinkAdapter } from "../../../../telephony/TelephonyAdapter";
import { fromDateTimeLocal, shortTime, toDateTimeLocal } from "../../../../utils/callQueue";
import { callSeconds, initialLiveCall, liveCallReducer } from "../../../../utils/liveCall";
import ConfirmChecklist, { ChecklistAnswers, emptyChecklist } from "./current/ConfirmChecklist";
import CallHeader from "./current/CallHeader";
import MaxBreakdown, { BreakdownLookups } from "./current/MaxBreakdown";
import ObjectionCards from "./current/ObjectionCards";
import OfferPanel from "./current/OfferPanel";
import PartsEstimateBreakdown from "./current/PartsEstimateBreakdown";
import QuotesHeaderStrip from "./header/QuotesHeaderStrip";
import CallOutcomePanel, { OutcomeDraft } from "./outcome/CallOutcomePanel";
import LeadHistory from "./outcome/LeadHistory";
import UpNextQueue from "./queue/UpNextQueue";

const AUTO_DIAL_KEY = "wilma:liveQueue:autoDial";
const AUTO_DIAL_SECONDS = 10;
// Well inside the API's hold (10 minutes by default), so a long call never lapses.
const HOLD_RENEW_MS = 60_000;

const emptyDraft: OutcomeDraft = {
  outcome: null,
  callBackAt: "",
  assignToMe: true,
  customerFigure: "",
  referToCustomerId: "",
  notes: "",
};

function readAutoDial() {
  try {
    return localStorage.getItem(AUTO_DIAL_KEY) !== "false";
  } catch {
    return true;
  }
}

function writeAutoDial(value: boolean) {
  try {
    localStorage.setItem(AUTO_DIAL_KEY, String(value));
  } catch {
    // Private windows can refuse storage; the checkbox still works for this visit.
  }
}

const money = (value: string) => {
  const n = Number(value);
  return value.trim() !== "" && Number.isFinite(n) && n > 0 ? n : null;
};

export default function LiveQueueScreen() {
  const { t } = useTranslation("leads");
  const { customerId } = useAuth();
  const isSavCustomer = !!customerId && customerId === wilmaSAVCustomerId;
  const { customerNames, allocateAtf } = useSav(isSavCustomer ? customerId : undefined, !isSavCustomer, "LiveQueueScreen");
  const atfNames = useMemo(() => customerNames.data?.data ?? [], [customerNames.data]);

  const [filter, setFilter] = useState<UpNextFilter>(UpNextFilter.AllDue);
  const { overview, serveNext, openLead, refreshSheet, logOutcome, requestApproval, refreshCatPrice, renewHold, updateContact } = useCallQueue(filter, true, "LiveQueueScreen");
  const { enginesWorldwideRefresh } = useVehiclesMutations();

  const [sheet, setSheet] = useState<ICallSheet | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [call, dispatch] = useReducer(liveCallReducer, initialLiveCall);
  const [now, setNow] = useState(() => new Date());
  const [agreedPrice, setAgreedPrice] = useState("");
  const [checklist, setChecklist] = useState<ChecklistAnswers>(emptyChecklist);
  const [draft, setDraft] = useState<OutcomeDraft>(emptyDraft);
  const [autoDial, setAutoDialState] = useState(readAutoDial);
  const [countdown, setCountdown] = useState<number | null>(null);
  const [busy, setBusy] = useState<BreakdownLookups["busy"]>(null);
  const [showEstimate, setShowEstimate] = useState(false);
  const telephony = useMemo<TelephonyAdapter>(() => new TelLinkAdapter(), []);
  const pendingDial = useRef<ICallSheet | null>(null);

  const { getVehiclePackageData } = useVehiclePackageData("", "LiveQueueScreen");
  const estimate = useInitialPriceEstimate(sheet?.leadId, "LiveQueueScreen");

  const data = overview.data?.data;

  const setAutoDial = (value: boolean) => {
    setAutoDialState(value);
    writeAutoDial(value);
  };

  // A new lead starts with a clean outcome form; a refresh of the same lead (after a lookup) keeps it.
  const showSheet = useCallback((next: ICallSheet, isNewLead: boolean) => {
    setSheet(next);
    setError(null);
    if (!isNewLead) return;

    setNotice(null);
    setShowEstimate(false);
    setAgreedPrice(next.quotedPrice.toFixed(2));
    setChecklist(emptyChecklist);
    setDraft({ ...emptyDraft, callBackAt: toDateTimeLocal(new Date(next.callBackDefault)) });
    dispatch({ type: "reset" });
  }, []);

  // Coming back to the page mid-call picks the same lead up again.
  useEffect(() => {
    if (sheet || !data?.servedLeadId) return;
    refreshSheet.mutateAsync(data.servedLeadId).then((res) => {
      if (res.success && res.data) showSheet(res.data, true);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.servedLeadId]);

  // The on-call timer.
  useEffect(() => {
    if (call.phase !== "onCall" && call.phase !== "ringing") return;
    const id = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(id);
  }, [call.phase]);

  // Renew the hold before dialling: a lead left idle past its hold may have gone to a colleague, and
  // then neither of us should ring the customer twice.
  const dial = useCallback(async (target: ICallSheet | null) => {
    if (!target?.telephone) return;
    const held = await renewHold.mutateAsync(target.leadId).catch(() => null);
    if (!held?.success) {
      setSheet(null);
      dispatch({ type: "reset" });
      setNotice(held?.message ?? t("This lead couldn't be kept for you. Take the next call."));
      return;
    }
    telephony.dial(target.telephone);
    dispatch({ type: "dial", at: new Date().toISOString() });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [telephony, t]);

  // The heartbeat: while a call is going on, keep renewing the hold so a long call never lapses.
  useEffect(() => {
    if (!sheet || call.phase === "available") return;
    const leadId = sheet.leadId;
    const id = window.setInterval(async () => {
      const held = await renewHold.mutateAsync(leadId).catch(() => null);
      // Mid-call, keep the screen up: the agent is on the phone. Saving will be refused, so say why.
      if (held && !held.success) setError(held.message);
    }, HOLD_RENEW_MS);
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sheet?.leadId, call.phase === "available"]);

  // Auto-dial: count down after Save & next, then dial unless the agent cancels.
  useEffect(() => {
    if (countdown === null) return;
    if (countdown <= 0) {
      setCountdown(null);
      dial(pendingDial.current);
      pendingDial.current = null;
      return;
    }
    const id = window.setTimeout(() => setCountdown((n) => (n === null ? null : n - 1)), 1000);
    return () => window.clearTimeout(id);
  }, [countdown, dial]);

  // When an estimate the agent asked for finishes, the max changes, so pull the sheet again.
  useEffect(() => {
    if (sheet && estimate.latest?.status === "Completed" && !sheet.partsEstimate) {
      refreshSheet.mutateAsync(sheet.leadId).then((res) => res.success && res.data && showSheet(res.data, false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estimate.latest?.status]);

  const takeNext = async () => {
    setError(null);
    const res = await serveNext.mutateAsync();
    if (res.success && res.data) {
      showSheet(res.data, true);
      return res.data;
    }
    setSheet(null);
    setNotice(res.message);
    return null;
  };

  const open = async (leadId: string) => {
    if (call.phase === "ringing" || call.phase === "onCall") {
      setError(t("Finish this call before opening another lead."));
      return;
    }
    const res = await openLead.mutateAsync(leadId);
    if (res.success && res.data) showSheet(res.data, true);
    else setError(res.message);
  };

  const save = async (outcomeOverride?: CallOutcome) => {
    if (!sheet) return;
    const outcome = outcomeOverride ?? draft.outcome;
    if (outcome === null) return;
    setError(null);

    try {
      const referTo = outcome === CallOutcome.Booked && isSavCustomer && draft.referToCustomerId ? draft.referToCustomerId : null;
      if (referTo) {
        const allocated = await allocateAtf.mutateAsync({ leadId: sheet.leadId, targetCustomerId: referTo });
        if (!allocated.success) {
          setError(allocated.message || t("The referral didn't go through. Try again."));
          return;
        }
      }

      const callBackAt = fromDateTimeLocal(draft.callBackAt);
      // A price changed in Your offer is the last price offered, whatever the outcome. One above the
      // booking limit is a request to a manager, not an offer, so it isn't sent.
      const offered = money(agreedPrice);
      const res = await logOutcome.mutateAsync({
        leadId: sheet.leadId,
        body: {
          outcome,
          startedOn: call.startedAt,
          agreedPrice: outcome === CallOutcome.Booked ? offered : null,
          offeredPrice: outcome !== CallOutcome.Booked && offered !== null && offered <= sheet.bookingLimit ? offered : null,
          customerFigure: outcome === CallOutcome.PriceTooLow ? money(draft.customerFigure) : null,
          callBackAt: outcome === CallOutcome.CallBack && callBackAt ? callBackAt.toISOString() : null,
          assignCallBackToMe: draft.assignToMe,
          referredToCustomerId: referTo,
          notes: draft.notes.trim() || null,
          ...checklist,
        },
      });

      if (!res.success) {
        setError(res.message);
        return;
      }

      dispatch({ type: "reset" });
      const next = await takeNext();
      if (next && autoDial && next.telephone) {
        pendingDial.current = next;
        setCountdown(AUTO_DIAL_SECONDS);
      }
    } catch (e: any) {
      setError(e?.message ?? t("Something went wrong saving the call."));
    }
  };

  const askManager = async (price: number, note: string) => {
    if (!sheet) return;
    const res = await requestApproval.mutateAsync({ leadId: sheet.leadId, body: { requestedPrice: price, note } });
    if (!res.success) {
      setError(res.message);
      return;
    }
    setDraft((d) => ({ ...d, outcome: CallOutcome.CallBack }));
    setNotice(t("Sent to a manager. Save a call back so the customer knows you'll ring them."));
    const refreshed = await refreshSheet.mutateAsync(sheet.leadId);
    if (refreshed.success && refreshed.data) showSheet(refreshed.data, false);
  };

  const saveContact = async (telephone: string, email: string) => {
    if (!sheet) return false;
    setError(null);
    const res = await updateContact.mutateAsync({ leadId: sheet.leadId, body: { telephone: telephone || null, email: email || null } });
    if (res.success && res.data) {
      showSheet(res.data, false);
      return true;
    }
    setError(res.message);
    return false;
  };

  const lookup = async (kind: NonNullable<BreakdownLookups["busy"]>) => {
    if (!sheet) return;
    setBusy(kind);
    try {
      if (kind === "cat") {
        const res = await refreshCatPrice.mutateAsync(sheet.leadId);
        if (res.success && res.data) showSheet(res.data, false);
        else setError(res.message);
        return;
      }
      if (kind === "export") await enginesWorldwideRefresh.mutateAsync(sheet.vehicleId);
      if (kind === "valuation") await getVehiclePackageData.mutateAsync({ vehicleId: sheet.vehicleId, package: VehiclePackage.JustSalvage });
      if (kind === "estimate") await estimate.run.mutateAsync();

      const res = await refreshSheet.mutateAsync(sheet.leadId);
      if (res.success && res.data) showSheet(res.data, false);
    } catch (e: any) {
      setError(e?.message ?? t("The lookup didn't answer. Try again in a moment."));
    } finally {
      setBusy(null);
    }
  };

  // The breakdown is the run the parts figure came from, so only a completed one is shown.
  const completedEstimate = estimate.latest?.status === "Completed" ? estimate.latest : undefined;
  const atfName = sheet?.atfCustomerId ? atfNames.find((x) => x.id === sheet.atfCustomerId)?.name : null;
  const seconds = callSeconds(call, now);
  const isSaving = logOutcome.isLoading || serveNext.isLoading || allocateAtf.isLoading;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-end gap-4">
        <QuotesHeaderStrip stats={data?.stats} phase={call.phase} seconds={seconds} />
      </div>

      <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-[20rem_minmax(0,1fr)] xl:grid-cols-[20rem_minmax(0,1fr)_22rem]">
        <UpNextQueue
          overview={data}
          filter={filter}
          setFilter={setFilter}
          onOpen={open}
          atfNames={atfNames}
          isLoading={overview.isLoading}
        />

        <section aria-label={t("Current call")} className="flex min-w-0 flex-col gap-4">
          {notice && <p className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">{notice}</p>}

          {!sheet ? (
            <div className="flex flex-col items-center gap-3 rounded-xl border border-gray-200 bg-white px-6 py-12 text-center shadow-sm">
              <PhoneArrowUpRightIcon className="h-8 w-8 text-gray-400" aria-hidden="true" />
              <h2 className="text-lg font-semibold text-gray-900">{t("Ready for the next call?")}</h2>
              <p className="max-w-md text-sm text-gray-600">
                {data && !data.insideCallingHours
                  ? t("Calls are only served between {{start}} and {{end}}.", { start: shortTime(data.callingHoursStart), end: shortTime(data.callingHoursEnd) })
                  : t("You'll be given the most urgent lead in the queue, with the price you can offer.")}
              </p>
              <button
                type="button"
                onClick={takeNext}
                disabled={serveNext.isLoading}
                className="min-h-[44px] rounded-md border border-primary-600 bg-primary-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-primary-700 disabled:opacity-50"
              >
                {serveNext.isLoading ? t("Finding a lead…") : t("Take next call")}
              </button>
              {error && <p className="text-sm text-red-700" role="alert">{error}</p>}
            </div>
          ) : (
            <>
              <CallHeader
                sheet={sheet}
                atfName={atfName}
                phase={call.phase}
                seconds={seconds}
                capabilities={telephony.capabilities}
                onDial={() => dial(sheet)}
                onAnswered={() => dispatch({ type: "answered", at: new Date().toISOString() })}
                onEnd={() => dispatch({ type: "end", at: new Date().toISOString() })}
                onHold={() => telephony.hold()}
                onTransfer={() => telephony.transfer("")}
                onSaveContact={saveContact}
                isSavingContact={updateContact.isLoading}
              />
              <OfferPanel
                sheet={sheet}
                agreedPrice={agreedPrice}
                setAgreedPrice={setAgreedPrice}
                onBook={() => {
                  setDraft((d) => ({ ...d, outcome: CallOutcome.Booked }));
                  // SAV picks who collects before saving; everyone else books straight away.
                  if (!isSavCustomer) save(CallOutcome.Booked);
                }}
                onAskManager={askManager}
                isSaving={isSaving || requestApproval.isLoading}
              />
              <div className="flex flex-wrap items-stretch gap-4">
                <ConfirmChecklist answers={checklist} setAnswers={setChecklist} postcode={sheet.postcode} floor={sheet.offer.floor} />
                <MaxBreakdown
                  sheet={sheet}
                  lookups={{
                    onExport: () => lookup("export"),
                    onCat: () => lookup("cat"),
                    onValuation: () => lookup("valuation"),
                    onPartsEstimate: () => lookup("estimate"),
                    busy,
                  }}
                  breakdownOpen={showEstimate}
                  onToggleBreakdown={completedEstimate ? () => setShowEstimate((x) => !x) : undefined}
                />
              </div>
              {showEstimate && completedEstimate && (
                <PartsEstimateBreakdown
                  estimate={completedEstimate}
                  showRejected={estimate.availability.data?.data?.showRejected ?? false}
                  onClose={() => setShowEstimate(false)}
                />
              )}
              <ObjectionCards cards={sheet.objections} />
            </>
          )}
        </section>

        {sheet && (
          <aside aria-label={t("Call outcome")} className="flex flex-col gap-4 lg:col-start-2 xl:col-start-3 xl:row-start-1">
            <CallOutcomePanel
              draft={draft}
              setDraft={setDraft}
              agreedPrice={agreedPrice}
              isSav={isSavCustomer}
              atfNames={atfNames}
              autoDial={autoDial}
              setAutoDial={setAutoDial}
              countdown={countdown}
              onCancelCountdown={() => {
                setCountdown(null);
                pendingDial.current = null;
              }}
              onSave={() => save()}
              isSaving={isSaving}
              error={error}
              disabled={false}
            />
            <LeadHistory entries={sheet.history} atfName={atfName} />
          </aside>
        )}
      </div>
    </div>
  );
}
